import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import crmService from "../services/crmService";

// CRM Staff Thunks
export const fetchStaffUsers = createAsyncThunk(
  "crm/fetchStaffUsers",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await crmService.getStaffUsers(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch staff users");
    }
  },
);

// User (Staff) Thunks
// User (Staff) Thunks
export const fetchUsers = createAsyncThunk(
  "crm/fetchUsers",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await crmService.getUsers(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch users");
    }
  },
);

// Campaign Thunks
export const fetchCampaigns = createAsyncThunk(
  "crm/fetchCampaigns",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await crmService.getCampaigns(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch campaigns");
    }
  },
);

export const fetchCampaignById = createAsyncThunk(
  "crm/fetchCampaignById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await crmService.getCampaignById(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch campaign");
    }
  },
);

export const createCampaign = createAsyncThunk(
  "crm/createCampaign",
  async (campaignData, { rejectWithValue }) => {
    try {
      const response = await crmService.createCampaign(campaignData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to create campaign");
    }
  },
);

export const updateCampaign = createAsyncThunk(
  "crm/updateCampaign",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await crmService.updateCampaign(id, data);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to update campaign");
    }
  },
);

export const deleteCampaign = createAsyncThunk(
  "crm/deleteCampaign",
  async (id, { rejectWithValue }) => {
    try {
      const response = await crmService.deleteCampaign(id);
      return { id, ...response };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to delete campaign");
    }
  },
);

// Kanban Status Thunks
export const fetchKanbanStatuses = createAsyncThunk(
  "crm/fetchKanbanStatuses",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await crmService.getKanbanStatuses(params);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to fetch kanban statuses",
      );
    }
  },
);

export const fetchKanbanStatusById = createAsyncThunk(
  "crm/fetchKanbanStatusById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await crmService.getKanbanStatusById(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch kanban status");
    }
  },
);

export const createKanbanStatus = createAsyncThunk(
  "crm/createKanbanStatus",
  async (statusData, { rejectWithValue }) => {
    try {
      const response = await crmService.createKanbanStatus(statusData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to create kanban status");
    }
  },
);

export const updateKanbanStatus = createAsyncThunk(
  "crm/updateKanbanStatus",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await crmService.updateKanbanStatus(id, data);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to update kanban status");
    }
  },
);

export const deleteKanbanStatus = createAsyncThunk(
  "crm/deleteKanbanStatus",
  async (id, { rejectWithValue }) => {
    try {
      const response = await crmService.deleteKanbanStatus(id);
      return { id, ...response };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to delete kanban status");
    }
  },
);

export const reorderKanbanStatuses = createAsyncThunk(
  "crm/reorderKanbanStatuses",
  async ({ campaignId, statusIds }, { rejectWithValue }) => {
    try {
      const response = await crmService.reorderKanbanStatuses(
        campaignId,
        statusIds,
      );
      return response;
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to reorder kanban statuses",
      );
    }
  },
);

// Lead Thunks
export const fetchLeads = createAsyncThunk(
  "crm/fetchLeads",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await crmService.getLeads(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch leads");
    }
  },
);

export const fetchLeadById = createAsyncThunk(
  "crm/fetchLeadById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await crmService.getLeadById(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch lead");
    }
  },
);

export const createLead = createAsyncThunk(
  "crm/createLead",
  async (leadData, { rejectWithValue }) => {
    try {
      const response = await crmService.createLead(leadData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to create lead");
    }
  },
);

export const updateLead = createAsyncThunk(
  "crm/updateLead",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await crmService.updateLead(id, data);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to update lead");
    }
  },
);

export const deleteLead = createAsyncThunk(
  "crm/deleteLead",
  async (id, { rejectWithValue }) => {
    try {
      const response = await crmService.deleteLead(id);
      return { id, ...response };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to delete lead");
    }
  },
);

export const assignLead = createAsyncThunk(
  "crm/assignLead",
  async ({ id, assignedTo }, { rejectWithValue }) => {
    try {
      const response = await crmService.assignLead(id, assignedTo);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to assign lead");
    }
  },
);

export const unassignLead = createAsyncThunk(
  "crm/unassignLead",
  async (id, { rejectWithValue }) => {
    try {
      const response = await crmService.unassignLead(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to unassign lead");
    }
  },
);

export const moveLead = createAsyncThunk(
  "crm/moveLead",
  async ({ id, statusId }, { rejectWithValue }) => {
    try {
      const response = await crmService.moveLead(id, statusId);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to move lead");
    }
  },
);

// Lead Upload Thunks
export const uploadLeadsFromCSV = createAsyncThunk(
  "crm/uploadLeadsFromCSV",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await crmService.uploadLeadsFromCSV(formData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to upload leads");
    }
  },
);

export const bulkUploadLeads = createAsyncThunk(
  "crm/bulkUploadLeads",
  async (leads, { rejectWithValue }) => {
    try {
      const response = await crmService.bulkUploadLeads(leads);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to bulk upload leads");
    }
  },
);

// Kanban Board Thunks
export const fetchKanbanBoard = createAsyncThunk(
  "crm/fetchKanbanBoard",
  async (campaignId, { rejectWithValue }) => {
    try {
      const response = await crmService.getKanbanBoard(campaignId);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch kanban board");
    }
  },
);

export const fetchAllKanbanBoards = createAsyncThunk(
  "crm/fetchAllKanbanBoards",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await crmService.getAllKanbanBoards(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch kanban boards");
    }
  },
);

export const fetchKanbanStats = createAsyncThunk(
  "crm/fetchKanbanStats",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await crmService.getKanbanStats(params);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to fetch kanban statistics",
      );
    }
  },
);

// Initial state
const initialState = {
  campaigns: [],
  currentCampaign: null,
  kanbanStatuses: [],
  currentKanbanStatus: null,
  leads: [],
  currentLead: null,
  kanbanBoard: null,
  kanbanBoards: [],
  kanbanStats: null,
  users: [],
  loading: false,
  error: null,
  uploadProgress: 0,
};

// Slice
const crmSlice = createSlice({
  name: "crm",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentCampaign: (state, action) => {
      state.currentCampaign = action.payload;
    },
    setCurrentKanbanStatus: (state, action) => {
      state.currentKanbanStatus = action.payload;
    },
    setCurrentLead: (state, action) => {
      state.currentLead = action.payload;
    },
    clearCurrentItems: (state) => {
      state.currentCampaign = null;
      state.currentKanbanStatus = null;
      state.currentLead = null;
    },
    updateLocalLead: (state, action) => {
      const { id, data } = action.payload;
      const index = state.leads.findIndex((lead) => lead._id === id);
      if (index !== -1) {
        state.leads[index] = { ...state.leads[index], ...data };
      }
      if (state.currentLead && state.currentLead._id === id) {
        state.currentLead = { ...state.currentLead, ...data };
      }
    },
    setUploadProgress: (state, action) => {
      state.uploadProgress = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Users (Staff) Actions
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users =
          action.payload.data?.users || action.payload.users || action.payload;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // CRM Staff Users Actions
      .addCase(fetchStaffUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStaffUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users =
          action.payload.data?.users || action.payload.users || action.payload;
      })
      .addCase(fetchStaffUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
    builder
      // Campaign Actions
      .addCase(fetchCampaigns.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCampaigns.fulfilled, (state, action) => {
        state.loading = false;
        state.campaigns = action.payload.data || action.payload;
      })
      .addCase(fetchCampaigns.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchCampaignById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCampaignById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentCampaign = action.payload.data || action.payload;
      })
      .addCase(fetchCampaignById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createCampaign.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCampaign.fulfilled, (state, action) => {
        state.loading = false;
        state.campaigns.unshift(action.payload.data || action.payload);
      })
      .addCase(createCampaign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateCampaign.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCampaign.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.campaigns.findIndex(
          (c) => c._id === (action.payload.data?._id || action.payload._id),
        );
        if (index !== -1) {
          state.campaigns[index] = action.payload.data || action.payload;
        }
        if (
          state.currentCampaign &&
          state.currentCampaign._id ===
            (action.payload.data?._id || action.payload._id)
        ) {
          state.currentCampaign = action.payload.data || action.payload;
        }
      })
      .addCase(updateCampaign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteCampaign.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCampaign.fulfilled, (state, action) => {
        state.loading = false;
        state.campaigns = state.campaigns.filter(
          (c) => c._id !== action.payload.id,
        );
        if (
          state.currentCampaign &&
          state.currentCampaign._id === action.payload.id
        ) {
          state.currentCampaign = null;
        }
      })
      .addCase(deleteCampaign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Kanban Status Actions
      .addCase(fetchKanbanStatuses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchKanbanStatuses.fulfilled, (state, action) => {
        state.loading = false;
        state.kanbanStatuses = action.payload.data || action.payload;
      })
      .addCase(fetchKanbanStatuses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchKanbanStatusById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchKanbanStatusById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentKanbanStatus = action.payload.data || action.payload;
      })
      .addCase(fetchKanbanStatusById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createKanbanStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createKanbanStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.kanbanStatuses.push(action.payload.data || action.payload);
        // Sort by sequence
        state.kanbanStatuses.sort((a, b) => a.sequence - b.sequence);
      })
      .addCase(createKanbanStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateKanbanStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateKanbanStatus.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.kanbanStatuses.findIndex(
          (s) => s._id === (action.payload.data?._id || action.payload._id),
        );
        if (index !== -1) {
          state.kanbanStatuses[index] = action.payload.data || action.payload;
        }
        // Sort by sequence
        state.kanbanStatuses.sort((a, b) => a.sequence - b.sequence);
        if (
          state.currentKanbanStatus &&
          state.currentKanbanStatus._id ===
            (action.payload.data?._id || action.payload._id)
        ) {
          state.currentKanbanStatus = action.payload.data || action.payload;
        }
      })
      .addCase(updateKanbanStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteKanbanStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteKanbanStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.kanbanStatuses = state.kanbanStatuses.filter(
          (s) => s._id !== action.payload.id,
        );
        if (
          state.currentKanbanStatus &&
          state.currentKanbanStatus._id === action.payload.id
        ) {
          state.currentKanbanStatus = null;
        }
      })
      .addCase(deleteKanbanStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(reorderKanbanStatuses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(reorderKanbanStatuses.fulfilled, (state, action) => {
        state.loading = false;
        state.kanbanStatuses = action.payload.data || action.payload;
      })
      .addCase(reorderKanbanStatuses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Lead Actions
      .addCase(fetchLeads.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLeads.fulfilled, (state, action) => {
        state.loading = false;
        state.leads = action.payload.data || action.payload;
      })
      .addCase(fetchLeads.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchLeadById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLeadById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentLead = action.payload.data || action.payload;
      })
      .addCase(fetchLeadById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createLead.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createLead.fulfilled, (state, action) => {
        state.loading = false;
        state.leads.unshift(action.payload.data || action.payload);
      })
      .addCase(createLead.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateLead.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateLead.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.leads.findIndex(
          (l) => l._id === (action.payload.data?._id || action.payload._id),
        );
        if (index !== -1) {
          state.leads[index] = action.payload.data || action.payload;
        }
        if (
          state.currentLead &&
          state.currentLead._id ===
            (action.payload.data?._id || action.payload._id)
        ) {
          state.currentLead = action.payload.data || action.payload;
        }
      })
      .addCase(updateLead.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteLead.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteLead.fulfilled, (state, action) => {
        state.loading = false;
        state.leads = state.leads.filter((l) => l._id !== action.payload.id);
        if (state.currentLead && state.currentLead._id === action.payload.id) {
          state.currentLead = null;
        }
      })
      .addCase(deleteLead.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(assignLead.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(assignLead.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.leads.findIndex(
          (l) => l._id === (action.payload.data?._id || action.payload._id),
        );
        if (index !== -1) {
          state.leads[index] = action.payload.data || action.payload;
        }
        if (
          state.currentLead &&
          state.currentLead._id ===
            (action.payload.data?._id || action.payload._id)
        ) {
          state.currentLead = action.payload.data || action.payload;
        }
      })
      .addCase(assignLead.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(unassignLead.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(unassignLead.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.leads.findIndex(
          (l) => l._id === (action.payload.data?._id || action.payload._id),
        );
        if (index !== -1) {
          state.leads[index] = action.payload.data || action.payload;
        }
        if (
          state.currentLead &&
          state.currentLead._id ===
            (action.payload.data?._id || action.payload._id)
        ) {
          state.currentLead = action.payload.data || action.payload;
        }
      })
      .addCase(unassignLead.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(moveLead.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(moveLead.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.leads.findIndex(
          (l) => l._id === (action.payload.data?._id || action.payload._id),
        );
        if (index !== -1) {
          state.leads[index] = action.payload.data || action.payload;
        }
        if (
          state.currentLead &&
          state.currentLead._id ===
            (action.payload.data?._id || action.payload._id)
        ) {
          state.currentLead = action.payload.data || action.payload;
        }
      })
      .addCase(moveLead.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Lead Upload Actions
      .addCase(uploadLeadsFromCSV.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.uploadProgress = 0;
      })
      .addCase(uploadLeadsFromCSV.fulfilled, (state, action) => {
        state.loading = false;
        state.uploadProgress = 100;
        // Add new leads to the list
        if (action.payload.leads) {
          state.leads.unshift(...action.payload.leads);
        }
      })
      .addCase(uploadLeadsFromCSV.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.uploadProgress = 0;
      })
      .addCase(bulkUploadLeads.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkUploadLeads.fulfilled, (state, action) => {
        state.loading = false;
        // Add new leads to the list
        if (action.payload.leads) {
          state.leads.unshift(...action.payload.leads);
        }
      })
      .addCase(bulkUploadLeads.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Kanban Board Actions
      .addCase(fetchKanbanBoard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchKanbanBoard.fulfilled, (state, action) => {
        state.loading = false;
        state.kanbanBoard = action.payload;
      })
      .addCase(fetchKanbanBoard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchAllKanbanBoards.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllKanbanBoards.fulfilled, (state, action) => {
        state.loading = false;
        state.kanbanBoards = action.payload.boards || action.payload;
      })
      .addCase(fetchAllKanbanBoards.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchKanbanStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchKanbanStats.fulfilled, (state, action) => {
        state.loading = false;
        state.kanbanStats = action.payload.stats || action.payload;
      })
      .addCase(fetchKanbanStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  setCurrentCampaign,
  setCurrentKanbanStatus,
  setCurrentLead,
  clearCurrentItems,
  updateLocalLead,
  setUploadProgress,
} = crmSlice.actions;

export default crmSlice.reducer;
